import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

// All main navigation is now route-based. Each link maps to a dedicated page.
const NAV_LINKS = [
    { to: "/ueber-mich", labelKey: "about" },
    { to: "/methodik", labelKey: "methods" },
    { to: "/dorn-breuss", labelKey: "therapy" },
    { to: "/programme", labelKey: "programs" },
    { to: "/kennenlerngespraech", labelKey: "booking" },
    { to: "/journal", labelKey: "blog" },
    { to: "/agb", labelKey: "agb" },
];

const Header = () => {
    const { lang, setLang, t } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goAnalyse = () => {
        setOpen(false);
        navigate("/analyse");
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled || !onHome
                    ? "bg-ella-cream/92 backdrop-blur-xl border-b border-ella-line/70 py-3"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="ella-container flex items-center justify-between">
                <Link
                    to="/"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="flex items-center gap-3"
                    data-testid="site-logo-link"
                >
                    <img
                        src={SITE.logoUrl}
                        alt="ELLA Online Coaching"
                        className="h-11 md:h-12 w-auto rounded-md object-contain"
                        data-testid="site-logo-image"
                    />
                    <div className="hidden sm:flex flex-col leading-none">
                        <span className="font-display text-lg tracking-[0.22em] text-ella-ink">
                            ELLA
                        </span>
                        <span className="ella-overline mt-1.5">
                            Online Coaching
                        </span>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-6">
                    {NAV_LINKS.map((link) => {
                        const isActive = location.pathname === link.to ||
                            (link.to === "/journal" && location.pathname.startsWith("/journal"));
                        return (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-route-${link.labelKey}`}
                                className={`text-[13px] tracking-wide hover:text-ella-gold-dark transition-colors duration-300 relative group ${
                                    isActive ? "text-ella-gold-dark" : "text-ella-ink/80"
                                }`}
                            >
                                {t.nav[link.labelKey]}
                                <span className="absolute -bottom-1 left-0 right-0 h-px bg-ella-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                            </Link>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3">
                    <div
                        className="hidden md:flex items-center gap-1 text-[11px] uppercase tracking-[0.24em] text-ella-muted"
                        data-testid="lang-switcher"
                    >
                        <button
                            onClick={() => setLang("de")}
                            data-testid="lang-de"
                            className={`px-1.5 transition ${
                                lang === "de" ? "text-ella-ink font-medium" : "hover:text-ella-ink"
                            }`}
                        >
                            DE
                        </button>
                        <span className="text-ella-line">·</span>
                        <button
                            onClick={() => setLang("es")}
                            data-testid="lang-es"
                            className={`px-1.5 transition ${
                                lang === "es" ? "text-ella-ink font-medium" : "hover:text-ella-ink"
                            }`}
                        >
                            ES
                        </button>
                    </div>

                    <button
                        onClick={goAnalyse}
                        className="hidden md:inline-flex btn-primary !px-5 !py-2.5 text-xs"
                        data-testid="header-cta-apply"
                    >
                        {t.nav.apply}
                    </button>

                    <button
                        className="xl:hidden p-2 rounded-full border border-ella-line text-ella-ink"
                        onClick={() => setOpen(!open)}
                        aria-label="menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden bg-ella-cream/98 backdrop-blur-xl border-t border-ella-line mt-3" data-testid="mobile-menu">
                    <nav className="ella-container flex flex-col py-6 gap-1">
                        {NAV_LINKS.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-route-${link.labelKey}`}
                                className="text-left py-3 border-b border-ella-line text-ella-ink font-sans"
                            >
                                {t.nav[link.labelKey]}
                            </Link>
                        ))}
                        <Link
                            to="/analyse"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-route-analyse"
                            className="text-left py-3 border-b border-ella-line text-ella-ink font-sans"
                        >
                            {t.nav.analysis}
                        </Link>
                        <div className="flex items-center gap-4 pt-4">
                            <button onClick={() => setLang("de")} data-testid="mobile-lang-de" className={`text-[11px] uppercase tracking-[0.24em] ${lang === "de" ? "text-ella-ink" : "text-ella-muted"}`}>Deutsch</button>
                            <span className="text-ella-line">·</span>
                            <button onClick={() => setLang("es")} data-testid="mobile-lang-es" className={`text-[11px] uppercase tracking-[0.24em] ${lang === "es" ? "text-ella-ink" : "text-ella-muted"}`}>Español</button>
                        </div>
                        <button onClick={goAnalyse} className="btn-primary mt-5" data-testid="mobile-cta-apply">
                            {t.nav.apply}
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
